import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DynamicHooksComponent } from 'ngx-dynamic-hooks';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SvgComponent } from 'icon';
import { LineCheckEligibleAccess } from 'interfaces';
import { ProductBoxComponent } from 'product-box';
import { ProductListItem } from 'product-shared';
import { SwiperComponent, SwiperItemDirective } from 'swiper';
import { TranslatePipe } from 'translate';
import { ProductsTabs } from '../../../../enums/products-tabs.enum';
import { LineCheckSlice } from '../line-check.slice';

interface ProductFilter {
  id: ProductsTabs;
  iconName: string;
  title?: string;
}

@Component({
  selector: 'app-line-check-result-list',
  templateUrl: './line-check-result-list.component.html',
  imports: [
    CommonModule,
    TranslatePipe,
    ProductBoxComponent,
    SwiperComponent,
    SwiperItemDirective,
    SvgComponent,
    DynamicHooksComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineCheckResultListComponent implements OnInit, OnDestroy {
  @Input() slice: LineCheckSlice;
  @Input() hasEligibleProduct: boolean;
  private _products: ProductListItem[];

  get products(): ProductListItem[] {
    return this._products;
  }

  @Input() set products(value: ProductListItem[]) {
    this._products = value;
    this.filteredTabs = this.getFilteredTabs(value);

    const featuredTab = this.filteredTabs.find(({ id }) => id === ProductsTabs.FEATURED);
    if (featuredTab) {
      this.activeFilterTab$.next(featuredTab);
    } else {
      const otherTabs = this.filteredTabs.filter(({ id }) => id !== ProductsTabs.ALL);
      if (otherTabs.length > 0) {
        this.activeFilterTab$.next(otherTabs[0]);
      } else {
        this.activeFilterTab$.next(this.filteredTabs.find(({ id }) => id === ProductsTabs.ALL));
      }
    }
  }

  @Input() set lineCheckEligibleAccess({ fiber, hfc, mbb }: LineCheckEligibleAccess) {
    this.networkAvailable = fiber || hfc || mbb;
  }

  private productFilters: ProductFilter[] = [
    { id: ProductsTabs.FEATURED, iconName: 'small-star' },
    { id: ProductsTabs.LOCAL, iconName: 'swiss-flag' },
    { id: ProductsTabs.GLOBAL, iconName: 'round-globe' },
    { id: ProductsTabs.PREPAID, iconName: 'sim-card' },
    { id: ProductsTabs.ALL, iconName: 'all_icon' },
  ];
  public initialSlideIndex = 0;
  public networkAvailable = true;
  public filteredTabs: ProductFilter[];
  public filteredProducts: ProductListItem[];
  public activeFilterTab$ = new BehaviorSubject<ProductFilter>(this.productFilters[0]);
  private subscription: Subscription;

  ngOnInit() {
    this.subscription = this.activeFilterTab$.subscribe(activeFilterTab => {
      this.filteredProducts = this.getFilteredProducts(activeFilterTab.id);
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private getFilteredTabs(products: ProductListItem[]): ProductFilter[] {
    const allowedFilters = new Set<ProductsTabs>();
    allowedFilters.add(ProductsTabs.ALL);
    for (const { featured, global, local, prepaid } of products) {
      if (featured) {
        allowedFilters.add(ProductsTabs.FEATURED);
      }
      if (local) {
        allowedFilters.add(ProductsTabs.LOCAL);
      }
      if (global) {
        allowedFilters.add(ProductsTabs.GLOBAL);
      }
      if (prepaid) {
        allowedFilters.add(ProductsTabs.PREPAID);
      }
    }
    return this.productFilters.filter(f => allowedFilters.has(f.id));
  }

  private getFilteredProducts(activeFilterId: ProductsTabs): ProductListItem[] {
    return this.products.filter(
      productListItem => activeFilterId === ProductsTabs.ALL || productListItem[activeFilterId]
    );
  }

  public onTabClick(productFilter: ProductFilter) {
    this.activeFilterTab$.next(productFilter);
  }
}
